/**
 * LiveActivity — premium multi-color streaming feed
 * ---------------------------------------------------------------------------
 * Each row picks a colour from a small palette so the feed reads like a
 * TradingView tape, not a monochrome list. The freshest event glows with
 * a subtle animated stripe.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Send, Sparkles, ShoppingBag, Zap, Crown } from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

const ROW_COLORS  = ['#00ff9d', '#4de0ff', '#c084fc', '#ff2d92', '#ffd34d', '#ff7a3d'];
const ROW_ICONS   = [ShoppingBag, Zap, Crown, Sparkles, Send];

const LiveActivity = () => {
  const { config } = useSiteConfig();
  const feed = useMemo(() => config.activity || [], [config.activity]);
  const [items, setItems] = useState(() => feed.slice(0, 1));

  useEffect(() => {
    if (feed.length === 0) return;
    let i = 1;
    const id = setInterval(() => {
      setItems(prev => [feed[i % feed.length], ...prev].slice(0, 4));
      i += 1;
    }, 3800);
    return () => clearInterval(id);
  }, [feed]);

  if (feed.length === 0) return null;

  return (
    <section className="relative py-14 sm:py-18 overflow-hidden">
      {/* Ambient blob (single, subtle) */}
      <div aria-hidden className="pointer-events-none absolute top-16 -right-16 w-72 h-72 rounded-full opacity-15 blur-3xl" style={{ background: '#ff2d92' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-6 sm:mb-8 flex-wrap gap-3">
          <div>
            <div className="eh-kicker mb-2">// LIVE_FEED · REAL-TIME</div>
            <h2 className="eh-display font-black" style={{ fontSize: 'clamp(1.5rem, 4.5vw, 2.6rem)' }}>
              RECENT <span className="eh-neon">ORDERS</span>
            </h2>
          </div>
          <div className="flex items-center gap-2 eh-mono text-[10px] tracking-widest opacity-90 px-3 py-1.5 rounded-full border" style={{ borderColor: 'rgba(0,255,157,.4)', background: 'rgba(0,255,157,.06)', color: '#00ff9d' }}>
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full opacity-70 animate-ping" style={{ background: '#00ff9d' }} />
              <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: '#00ff9d', boxShadow: '0 0 8px #00ff9d' }} />
            </span>
            STREAMING
          </div>
        </div>

        <div className="space-y-2">
          {items.map((a, i) => {
            const c = ROW_COLORS[Math.abs((a.id || '').charCodeAt(0) + i) % ROW_COLORS.length];
            const Icon = ROW_ICONS[Math.abs((a.id || '').charCodeAt(1) || 0) % ROW_ICONS.length];
            return (
              <div
                key={a.id + i}
                className="la-row relative rounded-xl border p-3 sm:p-4 flex items-center gap-3 overflow-hidden"
                style={{
                  borderColor: `${c}44`,
                  background: `linear-gradient(160deg, ${c}0d 0%, transparent 65%)`,
                  animation: i === 0 ? 'la-in .55s cubic-bezier(.2,.9,.3,1)' : 'none',
                  '--c': c,
                }}
                data-testid={`live-order-${i}`}
              >
                {/* Fresh-row top stripe */}
                {i === 0 && <span aria-hidden className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${c}, transparent)` }} />}
                <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0" style={{ background: `${c}1a`, border: `1px solid ${c}55`, color: c }}>
                  <Icon size={15} strokeWidth={1.9} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] leading-snug truncate" style={{ fontFamily: 'Inter,sans-serif' }}>
                    <span className="eh-mono text-[11.5px]" style={{ color: c }}>{a.user}</span>{' '}
                    <span className="opacity-70">just ordered</span>{' '}
                    <span className="font-bold">{a.service}</span>
                  </div>
                  <div className="eh-mono text-[10.5px] opacity-55 truncate">{a.location} · {a.ago}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="eh-mono text-[11px] font-bold px-2 py-1 rounded-md" style={{ background: `${c}18`, color: c, border: `1px solid ${c}44` }}>
                    ${a.amount}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <style>{`
          @keyframes la-in { from { transform: translateY(-6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          .la-row { transition: border-color .2s, transform .2s; }
          .la-row:hover { transform: translateX(2px); border-color: var(--c) !important; }
        `}</style>
      </div>
    </section>
  );
};

export default LiveActivity;
